import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useChat, type UIMessage } from '@ai-sdk/react';
import { DefaultChatTransport, isToolUIPart, getToolName } from 'ai';
import { hashKey, replaceEqualDeep, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useLatestRef } from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { env } from '@/config/env';
import { chatWithBliksyContract, type BliksyChatAttachment } from '@bliks-io/contracts';
import { useBliksyChatHistory } from '@/api/queries/useBliksyChatHistory';
import { useBliksyAttachments } from './useBliksyAttachments';
import { artifactKeys, bliksyKeys, externalContactKeys, recordingKeys } from '@/api/query-keys';
import {
  CHAT_STREAM_THROTTLE_MS,
  convertHistoryToUIMessages,
  prepareSendMessagesRequest
} from '@/components/chat/utils';

/** Tools that modify server data, mapped to the query keys they should invalidate. */
const DATA_MODIFYING_TOOLS: Record<string, (sessionId: string) => readonly unknown[]> = {
  modifyStepRecording: (sessionId) => recordingKeys.stepRecording(sessionId),
  createArtifact: () => artifactKeys.lists(),
  // createSystem/OrgFunctionSuggestion link the new suggestion onto the recording's
  // entitySuggestions[] via $addToSet, so the recording view (ghost chips) needs
  // to re-fetch to render them.
  createSystemSuggestion: (sessionId) => recordingKeys.stepRecording(sessionId),
  createOrgFunctionSuggestion: (sessionId) => recordingKeys.stepRecording(sessionId),
  createExternalContact: () => externalContactKeys.lists()
};

interface UseBliksyChatOptions {
  sessionId: string;
}

/**
 * Hook that manages Bliksy chat state and messaging using AI SDK's useChat.
 *
 * Key features:
 * - Streams responses from the backend using AI SDK patterns
 * - Loads and continues from existing chat history
 * - Invalidates recording query when changes are made
 * - Re-reads history once a stream settles, so the transcript ends up on the
 *   stored message ids rather than the client-minted ones
 */
export function useBliksyChat({ sessionId }: UseBliksyChatOptions) {
  const token = useAuthStore((s) => s.token);
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');

  // Load existing history from API
  const { data: historyData, isLoading: isHistoryLoading } = useBliksyChatHistory(sessionId);

  // Convert old history format to UIMessage[]
  const initialMessages = useMemo(
    () => (historyData ? convertHistoryToUIMessages(historyData) : []),
    [historyData]
  );

  // Memoize transport to avoid recreating on every render
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const attachments = useBliksyAttachments(sessionId);

  // The attachments of the turn being SENT. A ref, not state: the transport
  // reads it while the request is being built, in the same call that dispatches.
  // Every send overwrites it, so it never describes an older turn — and
  // `regenerate` re-sends the trailing turn, whose keys are exactly what it
  // still holds.
  const attachmentsRef = useRef<BliksyChatAttachment[]>([]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${env.BACKEND_ENDPOINT}${chatWithBliksyContract.path}`,
        // Function form: evaluated per-request to get fresh token
        headers: () => ({
          Authorization: `Bearer ${token}`
        }),
        prepareSendMessagesRequest: ({ messages }) =>
          prepareSendMessagesRequest(messages, sessionIdRef, attachmentsRef)
      }),
    [token]
  );

  // Mid-stream, staged keys flush on a trailing debounce so the recording view
  // updates while the answer is still being written. The SDK's `onFinish` flushes
  // the rest outside the render cycle: a proxy that buffers the SSE body lands the
  // whole turn in the render that settles the status, and a panel closed mid-turn
  // never renders it, so no render can be relied on to see the last tool output.
  const seenToolCallIds = useRef<Set<string>>(new Set());
  const pendingKeys = useRef<Map<string, readonly unknown[]>>(new Map());

  const flush = useCallback(() => {
    for (const queryKey of pendingKeys.current.values()) {
      queryClient.invalidateQueries({ queryKey });
    }
    pendingKeys.current.clear();
  }, [queryClient]);

  // 500ms matches the project's existing auto-save rhythm (useProcessRecording).
  // Trailing-only: last call in a burst wins; no leading fire to keep behaviour
  // consistent whether tool outputs arrive one-at-a-time or in rapid batches.
  const debouncedFlush = useMemo(() => debounce(flush, 500), [flush]);

  const stageToolOutputKeys = useCallback(
    (message: UIMessage | undefined) => {
      if (!message?.parts || message.role !== 'assistant') return false;
      let hasNew = false;
      for (const part of message.parts) {
        if (!isToolUIPart(part)) continue;
        if (part.state !== 'output-available') continue;
        if (seenToolCallIds.current.has(part.toolCallId)) continue;
        seenToolCallIds.current.add(part.toolCallId);
        const getQueryKey = DATA_MODIFYING_TOOLS[getToolName(part)];
        if (!getQueryKey) continue;
        const queryKey = getQueryKey(sessionId);
        pendingKeys.current.set(hashKey(queryKey), queryKey);
        hasNew = true;
      }
      return hasNew;
    },
    [sessionId]
  );

  const chat = useChat({
    id: sessionId, // Stable ID for this chat instance
    messages: initialMessages, // Initial messages from history
    transport,
    // Coalesces render notifications only — no stream data is dropped. Without it a
    // buffered burst of tool-input deltas trips React's nested-update guard and kills
    // the chat mid-stream. See CHAT_STREAM_THROTTLE_MS.
    throttle: CHAT_STREAM_THROTTLE_MS,
    onError: (error) => {
      console.error('Bliksy chat error:', error);
    },
    onFinish: ({ message, isError }) => {
      stageToolOutputKeys(message);
      debouncedFlush.cancel();
      flush();
      // Re-read history so the transcript lands on the stored `legacy:…` ids the
      // feedback vote needs (see the hook doc); an errored stream stored no turn.
      if (!isError) {
        queryClient.invalidateQueries({ queryKey: bliksyKeys.chatHistory(sessionId) });
      }
    }
  });

  const setMessages = chat.setMessages;

  // Read through a ref, deliberately NOT a dependency: with `chat.status` in the
  // deps below, the streaming→ready transition re-runs the reset against the
  // history fetched BEFORE the turn and flickers the fresh answer away. As a
  // ref it only decides whether a history payload landing mid-stream may replace
  // the transcript — it may not, or a focus-refetch would delete the answer
  // being written.
  const chatStatus = useLatestRef(chat.status);

  // Three jobs in one effect: seed the transcript when the session changes,
  // refuse to touch it while a stream is running, and RECONCILE rather than
  // replace when a settled history lands.
  useEffect(
    function seedTranscriptFromHistory() {
      if (chatStatus.current === 'submitted' || chatStatus.current === 'streaming') return;
      if (historyData && setMessages) {
        // Reconcile rather than replace: turns that are deep-equal keep their
        // object identity, so the memoised transcript re-renders only the turn
        // the settle-refetch actually canonicalized instead of re-parsing every
        // message's markdown.
        setMessages((previous) => replaceEqualDeep(previous, initialMessages));
      }
    },
    // `chatStatus` is the stable ref OBJECT — listed only because the lint rule
    // cannot see the `useRef` behind the custom hook. The status VALUE is
    // deliberately absent: nothing here re-runs when the stream changes state.
    [sessionId, historyData, initialMessages, setMessages, chatStatus]
  );

  // Reset state and cancel any pending flush when switching sessions or
  // unmounting. Prevents a stale debounced callback from firing against a
  // freshly-loaded chat. The composing keys go with it: a Bliksy key is
  // recording-scoped, so one left over from the previous recording could only
  // ever hard-reject at send.
  useEffect(
    function resetInvalidationStateOnSessionChange() {
      seenToolCallIds.current.clear();
      pendingKeys.current.clear();
      attachmentsRef.current = [];
      debouncedFlush.cancel();
      return () => debouncedFlush.cancel();
    },
    [sessionId, debouncedFlush]
  );

  const messages = chat.messages;
  useEffect(
    function collectQueryKeysFromToolOutputs() {
      if (chat.status !== 'streaming') return;
      if (stageToolOutputKeys(messages[messages.length - 1])) debouncedFlush();
    },
    [messages, chat.status, stageToolOutputKeys, debouncedFlush]
  );
  // Destructure stable function references from chat
  const { sendMessage } = chat;

  // Derive loading state from chat status
  const isLoading = chat.status === 'streaming' || chat.status === 'submitted';

  const { attachments: composed, isUploading, clear: clearAttachments } = attachments;

  /**
   * Send the composed turn.
   *
   * Nothing the user owns is cleared before the dispatch is accepted: on a
   * refusal the text and the readied files stay on screen, because clearing them
   * is how a message gets lost. A turn is refused while the stored transcript is
   * still loading — the greeting is a blind insert, and a turn sent against a
   * half-known history would be answered without it.
   */
  const handleSend = useCallback(() => {
    const text = input.trim();
    if ((!text && composed.length === 0) || isLoading || isHistoryLoading || isUploading) return;
    attachmentsRef.current = composed;
    // The files travel twice, deliberately: as file PARTS on the message so the
    // turn draws its chips immediately, and as storage KEYS in the request body,
    // because that is the only reference the backend accepts. The parts carry an
    // empty `url` — the object is private and the browser never holds it.
    sendMessage({
      text,
      files: composed.map((attachment) => ({
        type: 'file' as const,
        mediaType: attachment.mediaType,
        filename: attachment.filename,
        url: ''
      }))
    });
    setInput('');
    clearAttachments();
  }, [input, isLoading, isHistoryLoading, isUploading, composed, clearAttachments, sendMessage]);

  // Send message by text (for programmatic use)
  const sendMessageText = useCallback(
    (text: string) => {
      if (text.trim() && !isLoading) {
        attachmentsRef.current = [];
        sendMessage({ text: text.trim() });
      }
    },
    [isLoading, sendMessage]
  );

  return {
    // State
    messages: chat.messages as UIMessage[],
    inputValue: input,
    isLoading,
    isHistoryLoading,
    status: chat.status,
    error: chat.error,
    attachments,

    // Actions
    setInputValue: setInput,
    sendMessage: sendMessageText,
    handleSend,
    /** Retry the last failed request */
    retry: chat.regenerate
  };
}
